import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  coding_wiz_split_grouping_valid,
  type CodingWizPreferences,
} from "./coding_wiz_preferences"
import {
  prefs_from_split_grouping_value,
  SplitGroupingOptions,
  split_grouping_value_from_prefs,
  type SplitGroupingValue,
} from "./split_components/split_grouping_options"
import { useEffect, useState } from "preact/hooks"

export interface CodingWizRunFormProps {
  initial_prefs: CodingWizPreferences
  is_running?: boolean
  on_run: (prefs: CodingWizPreferences, instructions: string, save_preferences: boolean) => void
  className?: string
}

export function CodingWizRunForm({ initial_prefs, is_running, on_run, className }: CodingWizRunFormProps) {
  const [split_into_multiple_lines, set_split_into_multiple_lines] = useState(initial_prefs.split_into_multiple_lines)
  const [grouping, set_grouping] = useState<SplitGroupingValue>(split_grouping_value_from_prefs(initial_prefs))
  const [instructions_text, set_instructions_text] = useState("")
  const [save_preferences, set_save_preferences] = useState(false)

  useEffect(() => {
    set_split_into_multiple_lines(initial_prefs.split_into_multiple_lines)
    set_grouping(split_grouping_value_from_prefs(initial_prefs))
    set_instructions_text("")
    set_save_preferences(false)
  }, [initial_prefs])

  const draft_prefs = prefs_from_split_grouping_value(split_into_multiple_lines, grouping)
  const can_run =
    !is_running && (!split_into_multiple_lines || coding_wiz_split_grouping_valid(draft_prefs))

  function handle_run() {
    if (!can_run) {
      return
    }
    on_run(draft_prefs, instructions_text, save_preferences)
  }

  return (
    <div class={`space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-3 ${className ?? ""}`}>
      <div class="space-y-2">
        <Label class="text-sm font-medium">Specific instructions (optional)</Label>
        <Textarea
          value={instructions_text}
          onInput={(e) => set_instructions_text((e.target as HTMLTextAreaElement).value)}
          placeholder="Is there anything specific about this transaction that should be handled differently?"
          rows={3}
          disabled={!!is_running}
        />
      </div>

      <div class="space-y-2">
        <Label class="text-sm font-medium">Coding approach</Label>
        <div class="space-y-2">
          <CodingModeOption
            id="coding-wiz-single"
            name="coding-wiz-mode"
            label="Single ledger code"
            description="Assign one account code to this transaction."
            checked={!split_into_multiple_lines}
            disabled={!!is_running}
            on_select={() => set_split_into_multiple_lines(false)}
          />
          <CodingModeOption
            id="coding-wiz-split"
            name="coding-wiz-mode"
            label="Split into multiple lines"
            description="Create multiple split lines from the backup."
            checked={split_into_multiple_lines}
            disabled={!!is_running}
            on_select={() => set_split_into_multiple_lines(true)}
          />
        </div>
      </div>

      {split_into_multiple_lines ? (
        <SplitGroupingOptions value={grouping} onChange={set_grouping} />
      ) : null}

      {split_into_multiple_lines && !coding_wiz_split_grouping_valid(draft_prefs) ? (
        <p class="text-xs text-red-600">Select at least one grouping option for multi-line splits.</p>
      ) : null}

      <div class="flex items-center space-x-2">
        <Checkbox
          id="coding-wiz-save-prefs"
          checked={save_preferences}
          disabled={!!is_running}
          onCheckedChange={(checked) => set_save_preferences(!!checked)}
        />
        <Label htmlFor="coding-wiz-save-prefs" class="text-sm font-normal cursor-pointer">
          Save as the team default (used by bulk CodingWiz runs)
        </Label>
      </div>

      <div class="flex justify-end">
        <Button type="button" disabled={!can_run} onClick={handle_run}>
          {is_running ? "Running…" : "Run CodingWiz"}
        </Button>
      </div>
    </div>
  )
}

interface CodingModeOptionProps {
  id: string
  name: string
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  on_select: () => void
}

function CodingModeOption({
  id,
  name,
  label,
  description,
  checked,
  disabled,
  on_select,
}: CodingModeOptionProps) {
  return (
    <div class="flex items-start gap-2">
      <input
        type="radio"
        id={id}
        name={name}
        checked={checked}
        disabled={disabled}
        class="mt-1"
        onChange={on_select}
      />
      <Label htmlFor={id} class="text-sm font-normal cursor-pointer leading-snug">
        <span class="font-medium text-slate-900">{label}</span>
        <span class="block text-xs text-slate-600">{description}</span>
      </Label>
    </div>
  )
}
